import React, { useEffect, useState } from "react";
import useStyles from "./style";
import { useDisclosure } from "@mantine/hooks";
import { useSelector } from "react-redux";
import { Text } from "@react-pdf/renderer";
import TableLoader from "../TableLoader";
import { Box, Button, Flex, Input, Tabs } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import UploadStaffDoc from "./UploadStaffDoc";
import UploadFileSvg from "../Svgs/UploadFileSvg";
import { IconSearch } from "@tabler/icons-react";
import { StaffFileFolderRadio, StaffRowGridRadio } from "./StaffRowGridRadio";
import { StaffFileItem, StaffFileTable } from "./StaffFile";
import { StaffFolderItem, StaffFolderTable } from "./StaffFolder";
import { getFiles, getFolders } from "../../services/Document";
import RecordNotFound from "../RecordNotFound";
import CreateStaffFolderModal from "./CreateStaffFolderModal";
import useDebounce from "../../utils/useDebounce";
import UploadFolderSvg from "../Svgs/UploadFolderSvg";
import { MdKeyboardArrowLeft } from "react-icons/md";

/**
 * Component to manage staff documents.
 * @param {Object} props
 * @param {Number} props.useId - Contains the selected staff's data info.
 * @param {Sting} props.userType - The type of user. "staff" or "facility" or "clinet".
 */
export const StaffDocumentsTab = ({ userId, userType }) => {
  const { classes } = useStyles();
  const [type, setType] = useState("folder");
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [view, setView] = useState("grid");
  const [expiredTabLoading, setExpiredTabLoading] = useState(false);
  const [tabState, setTabState] = useState("active");
  const [opened, { open, close }] = useDisclosure(false);
  const { getFolderLoading, folders, files, getFileLoading } = useSelector(
    (state) => state.document
  );

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 2000);
  const [templateToUpload, setTemplateToUpload] = useState(null);

  const getFoldersData = async () => {
    if (debouncedSearch) {
      await getFolders(userId, userType, debouncedSearch);
    } else {
      await getFolders(userId, userType);
    }
  };

  const getFilesData = async () => {
    if (debouncedSearch) {
      await getFiles(userId, userType, selectedFolder?._id, debouncedSearch);
    } else {
      // await getFiles(userId, userType, selectedFolder?._id);
      const folderId = selectedFolder?._id || selectedFolder?.id;
      await getFiles(userId, userType, folderId);
    }
  };

  useEffect(() => {
    if (type === "folder") {
      getFoldersData();
    } else {
      getFilesData();
    }
  }, [type, selectedFolder, debouncedSearch]);

  useEffect(() => {
    if (tabState == "expired") {
      setExpiredTabLoading(true);
      setTimeout(() => {
        setExpiredTabLoading(false);
      }, 2000);
    }
  }, [tabState]);

  const filteredFolders = folders?.filter((folder) => {
    if (userType === "staff") return folder?.staff === userId;
    if (userType === "facility") return folder?.facility === userId;
    return false;
  });

  const GridView = () => {
    return (
      <div>
        {type === "file" ? (
          <>
          <Flex justify={"center"} align={"center"}>

            <Text my={5} ta={"center"} fw={"bold"} fz={18} ff={"Inter"}>
              {selectedFolder ? selectedFolder?.name : "All Documents"}
            </Text>
          </Flex>
            <Box
              style={{ justifyContent: files ? "start" : "center" }}
              className={classes.row}
            >
              {getFileLoading ? (
                <TableLoader />
              ) : files?.activeFiles?.length > 0 ||
                files?.expiredFiles?.length > 0 ||
                files?.folderInfo?.folders?.length > 0 ? (
                <>
                  <Tabs
                    value={tabState}
                    color="yellow"
                    style={{ width: "100%" }}
                    onTabChange={(e) => {
                      setTabState(e);
                    }}
                  >
                    <Tabs.List style={{ gap: "39px" }}>
                      <Tabs.Tab value="active">Active</Tabs.Tab>
                      <Tabs.Tab value="expired">Expired</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="active">
                      <Box className={classes.tabsPanels}>
                        {/* Always show folders if available */}
                        {files?.folderInfo &&
                          files?.folderInfo?.folders?.length > 0 &&
                          files?.folderInfo?.folders?.map((folder) => (
                            <StaffFolderItem
                              key={folder._id}
                              data={folder}
                              setSelectedFolder={setSelectedFolder}
                              setType={setType}
                              userId={userId}
                              userType={userType}
                            />
                          ))}

                        {expiredTabLoading ? (
                          <TableLoader color="yellow" size={"md"} />
                        ) : files?.activeFiles?.length > 0 ? (
                          files?.activeFiles?.map((file) => (
                            <StaffFileItem
                              data={file}
                              userId={userId}
                              userType={userType}
                              selectedFolder={selectedFolder}
                              onUploadTemplate={(tpl) => setTemplateToUpload(tpl)}
                            />
                          ))
                        ) : (
                          <Box className={classes.notFoundTextContainer}>
                            <RecordNotFound />
                            <Text className={classes.notFoundText}>
                              You don&apos;t have any files uploaded to this
                              file, click on "upload file" to add.
                            </Text>
                          </Box>
                        )}
                      </Box>
                    </Tabs.Panel>
                    <Tabs.Panel
                      value="expired"
                      onChange={(e) => console.log("expired tab click", e)}
                    >
                      <Box className={classes.tabsPanels}>
                        {expiredTabLoading ? (
                          <TableLoader color="yellow" size={"md"} />
                        ) : files?.expiredFiles?.length > 0 ? (
                          files?.expiredFiles?.map((file) => (
                            <StaffFileItem
                              data={file}
                              userId={userId}
                              userType={userType}
                              selectedFolder={selectedFolder}
                              onUploadTemplate={(tpl) => setTemplateToUpload(tpl)}
                            />
                          ))
                        ) : (
                          <Box className={classes.notFoundTextContainer}>
                            <RecordNotFound />
                            <Text className={classes.notFoundText}>
                              You don&apos;t have any files uploaded to this
                              folder, click on "upload documents" to add.
                            </Text>
                          </Box>
                        )}
                      </Box>
                    </Tabs.Panel>
                  </Tabs>
                </>
              ) : (
                <Box className={classes.notFoundTextContainer}>
                  <RecordNotFound />
                  <Text className={classes.notFoundText}>
                    You don&apos;t have any file, click on the upload file above
                    to add.
                  </Text>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <>
            {/* <Text my={5} ta={"center"}>
              All Folders
            </Text> */}
            <div
              className={classes.row}
              style={{
                justifyContent: folders?.length > 0 ? "start" : "center",
              }}
            >
              {getFolderLoading ? (
                <TableLoader />
              ) : filteredFolders?.length > 0 ? (
                filteredFolders
                  // ?.filter((folder) => folder?.staff === userId)
                  ?.map((folder) => (
                    <StaffFolderItem
                      key={folder._id}
                      data={folder}
                      setSelectedFolder={setSelectedFolder}
                      setType={setType}
                      userId={userId}
                      userType={userType}
                    />
                  ))
              ) : (
                <Box className={classes.notFoundTextContainer}>
                  <RecordNotFound />
                  <Text className={classes.notFoundText}>
                    You don&apos;t have any folders, click on the folder icon
                    above to add.
                  </Text>
                </Box>
              )}
            </div>
          </>
        )}
      </div>
    );
  };
  const TableView = () => {
    return (
      <div>
        {type === "file" ? (
          <>
            {/* <Text my={5} ta={"center"}>
              {selectedFolder ? selectedFolder?.name : "All Documents"}
            </Text> */}

            {getFileLoading ? (
              <TableLoader />
            ) : (
              // <StaffFileTable client={userId} data={files?.activeFiles} />
              <Tabs
                value={tabState}
                style={{ width: "100%" }}
                onTabChange={(e) => {
                  setTabState(e);
                }}
              >
                <Tabs.List style={{ gap: "39px" }}>
                  <Tabs.Tab value="active">Active</Tabs.Tab>
                  <Tabs.Tab value="expired">Expired</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="active">
                  {selectedFolder?.folders?.length > 0 &&
                    selectedFolder?.folders?.map((folder) => (
                      <StaffFolderItem
                        key={folder._id}
                        data={folder}
                        setSelectedFolder={setSelectedFolder}
                        setType={setType}
                        userId={userId}
                        userType={userType}
                      />
                    ))}
                  <StaffFileTable client={userId} data={files?.activeFiles} userId={userId} userType={userType} selectedFolder={selectedFolder} onUploadTemplate={(tpl) => setTemplateToUpload(tpl)} />
                </Tabs.Panel>
                <Tabs.Panel
                  value="expired"
                  onChange={(e) => console.log("expired tab click", e)}
                >
                  <StaffFileTable client={userId} data={files?.expiredFiles} userId={userId} userType={userType} selectedFolder={selectedFolder} onUploadTemplate={(tpl) => setTemplateToUpload(tpl)} />
                </Tabs.Panel>
              </Tabs>
            )}
          </>
        ) : (
          <>
            {/* <Text my={5} ta={"center"}>
              All Folders
            </Text> */}
            {getFolderLoading ? (
              <TableLoader />
            ) : (
              <StaffFolderTable
                setType={setType}
                setSelectedFolder={setSelectedFolder}
                folders={folders}
                filteredFolders={filteredFolders}
                userId={userId}
                userType={userType}
              />
            )}
          </>
        )}
      </div>
    );
  };
  return (
    <>
      <Box mt={20}>
        <Input
          radius={"md"}
          icon={<IconSearch />}
          placeholder="Search File, Folder"
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>
      <div className={classes.row} style={{justifyContent: "space-between", width: "100%"}}>
        <div className={classes.row}>
          <Button
            leftIcon={<UploadFolderSvg size={20} />}
            color="gray"
            variant="outline"
            onClick={() => {
              open();
            }}
            className={classes.iconButton}
            style={{ color: "black" }}
          >
            <Text>New Folder</Text>
          </Button>
          <UploadStaffDoc
            selectedFolder={selectedFolder}
            userId={userId}
            userType={userType}
          />
        </div>
        <StaffRowGridRadio onChange={(val) => setView(val)} />
      </div>
      {selectedFolder ? (
        <>
          {/* <Button
          onClick={() => {
            setSelectedFolder(null);
            setType("folder");
          }}
          color="yellow"
          className={classes.iconButton}
          >
          <IconArrowLeft /> Back to Folders
        </Button> */}
          <Box
            align="center"
            gap={5}
            m={10}
            onClick={() => {
              setSelectedFolder(null);
              setType("folder");
            }}
            style={{
              cursor: "pointer",
              width: "fit-content",
              display: "flex",
              alignItems: "center",
            }}
          >
            <MdKeyboardArrowLeft size={30} />
            <Text fw={400} fz={14} c={"#262626"} ff={"Inter"}>
              Back
            </Text>
          </Box>
        </>
      ) : (
        <StaffFileFolderRadio onChange={(val) => setType(val)} />
      )}
      {view === "grid" ? <GridView /> : <TableView />}
      <CreateStaffFolderModal
        opened={opened}
        onclose={() => {
          close();
        }}
        title={"New Folder"}
        userId={userId}
        userType={userType}
        selectedFolder={selectedFolder}
      />
      {templateToUpload && (
        <UploadStaffDoc
          key={templateToUpload?._id || templateToUpload?.id}
          selectedFolder={selectedFolder}
          userId={userId}
          userType={userType}
          isnotificationdoc={true}
          uploadedFiles={templateToUpload}
          onModalClose={() => setTemplateToUpload(null)}
        />
      )}
    </>
  );
};
